import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, TextField } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LiveIndicator } from "../../../components/LiveIndicator/LiveIndicator";
import { isHourSlotInFuture } from "../../../utilities/helpers/shiftHourHelpers";

type ShiftHourSelectProps = {
  hour?: number
  hours: string[]
  /** Index of the in-progress hour within `hours`, or -1 when no hour is live. */
  liveHourIndex: number
  disabled: boolean
  onChange: (hour: number) => unknown
}

const ShiftHourSelect: React.FC<ShiftHourSelectProps> = ({ hour, hours, liveHourIndex, disabled, onChange }) => {
  const { t } = useTranslation("geology");
  const [isWorking, setIsWorking] = useState(false);
  const selected = hour != null ? hours[hour - 1] ?? null : null;

  return (
    <Autocomplete
      classes={{ root: 'w-full' }}
      size="small"
      disabled={disabled || isWorking}
      value={selected}
      options={hours}
      getOptionDisabled={(option) => isHourSlotInFuture(hours.indexOf(option), liveHourIndex)}
      onChange={async (_, val) => {
        if (val == null) return;
        setIsWorking(true);
        await onChange(hours.indexOf(val) + 1);
        setIsWorking(false);
      }}
      renderInput={(params) => <TextField
        {...params}
        label={t("fields.hour")}
        slotProps={{
          ...params.slotProps,
          input: {
            ...params.slotProps.input,
            endAdornment: (
              <>
                {selected != null && hours.indexOf(selected) === liveHourIndex && <LiveIndicator size={8} />}
                {isWorking && <FontAwesomeIcon icon={faSpinner} spin />}
                {params.slotProps.input.endAdornment}
              </>
            )
          }
        }}
      />}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <span className="flex items-center justify-between w-full gap-2">
              <span>{option}</span>
              {hours.indexOf(option) === liveHourIndex && <LiveIndicator label={t("movement.live")} size={8} />}
            </span>
          </li>
        );
      }}
    />
  );
};

export default ShiftHourSelect;
